<!-- eslint-disable no-plusplus -->
<!-- eslint-disable max-len -->
<!-- eslint-disable default-case -->
<template>
  <div class="goals-page">
    <div class="content">
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-content">
          <div class="header-top">
            <button class="btn back-btn" @click="goBack">
              <i class="fas fa-arrow-left"></i>
              Back to Dashboard
            </button>
          </div>
          <h1 class="cabin-family bold">Financial Goals</h1>
          <p class="secondary-text">Track your progress towards financial freedom</p>
          <div class="interest-badge">
            <i class="fas fa-chart-line"></i>
            <span>Earn <strong>{{ goalInterestRate !== null ? goalInterestRate + '%' : '—' }} interest per annum</strong> on your saved goals</span>
          </div>
        </div>
        <div class="header-actions">
          <button
            class="btn primary-btn"
            @click="showCreateGoalModal = true"
          >
            <i class="fas fa-plus"></i>
            Create Goal
          </button>
        </div>
      </div>

      <!-- Goals Overview Cards -->
      <div class="overview-cards">
        <div class="overview-card">
          <div class="card-icon primary">
            <i class="fas fa-bullseye"></i>
          </div>
          <div class="card-content">
            <h3 class="cabin-family semi-bold">{{ activeGoals.length }}</h3>
            <p class="secondary-text">Active Goals</p>
          </div>
        </div>

        <div class="overview-card">
          <div class="card-icon success">
            <i class="fas fa-chart-line"></i>
          </div>
          <div class="card-content">
            <h3 class="cabin-family semi-bold">{{ overallProgress }}%</h3>
            <p class="secondary-text">Overall Progress</p>
          </div>
        </div>

        <div class="overview-card">
          <div class="card-icon primary">
            <i class="fas fa-wallet"></i>
          </div>
          <div class="card-content">
            <h3 class="cabin-family semi-bold">{{ formatCurrency(totalSavedAmount) }}</h3>
            <p class="secondary-text">Total Saved</p>
          </div>
        </div>

        <div class="overview-card">
          <div class="card-icon secondary">
            <i class="fas fa-trophy"></i>
          </div>
          <div class="card-content">
            <h3 class="cabin-family semi-bold">{{ badges.length }}</h3>
            <p class="secondary-text">Badges Earned</p>
          </div>
        </div>
      </div>

      <!-- Filter and Search -->
      <div class="goals-controls">
        <div class="search-filter">
          <div class="search-box">
            <i class="fas fa-search"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search goals..."
              class="form-control"
            />
          </div>

          <div class="filter-dropdown">
            <select v-model="selectedFilter" class="form-control">
              <option value="all">All Goals</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="on_track">On Track</option>
              <option value="behind">Behind Schedule</option>
            </select>
          </div>

          <div class="sort-dropdown">
            <select v-model="selectedSort" class="form-control">
              <option value="created_desc">Newest First</option>
              <option value="created_asc">Oldest First</option>
              <option value="progress_desc">Highest Progress</option>
              <option value="progress_asc">Lowest Progress</option>
              <option value="target_date_asc">Due Date</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Goals Content -->
      <div class="goals-content">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <LoadingSpinner size="large" text="Loading your goals..." />
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <div class="error-card">
            <i class="fas fa-exclamation-triangle"></i>
            <h3 class="cabin-family semi-bold">Unable to load goals</h3>
            <p>{{ error }}</p>
            <button class="btn primary-btn" @click="loadGoals">
              <i class="fas fa-redo"></i>
              Try Again
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="allFilteredGoals.length === 0 && !searchQuery" class="empty-state">
          <div class="empty-card glass-morphism">
            <i class="fas fa-bullseye"></i>
            <h3 class="cabin-family semi-bold">No goals yet</h3>
            <p>Create your first financial goal to get started on your journey to financial freedom.</p>
            <button
              class="btn primary-btn"
              @click="showCreateGoalModal = true"
            >
              <i class="fas fa-plus"></i>
              Create Your First Goal
            </button>
          </div>
        </div>

        <!-- No Search Results -->
        <div v-else-if="allFilteredGoals.length === 0 && searchQuery" class="no-results-state">
          <div class="empty-card glass-morphism">
            <i class="fas fa-search"></i>
            <h3 class="cabin-family semi-bold">No goals found</h3>
            <p>No goals match your search criteria. Try adjusting your filters.</p>
            <button class="btn btn-outline-secondary" @click="clearFilters">
              Clear Filters
            </button>
          </div>
        </div>

        <!-- Goals Grid -->
        <transition-group
          v-else
          name="goals-list"
          tag="div"
          class="goals-grid"
        >
          <GoalCard
            v-for="goal in paginatedGoals"
            :key="goal.id"
            :goal="goal"
            @contribute="handleContribute"
            @view-details="handleViewDetails"
          />
        </transition-group>

        <!-- Load More Button -->
        <div v-if="hasMoreGoals" class="load-more">
          <button
            class="btn btn-outline-primary"
            :disabled="loadingMore"
            @click="loadMoreGoals"
          >
            <LoadingSpinner v-if="loadingMore" size="small" />
            <span v-else>Load More Goals</span>
          </button>
        </div>
      </div>

      <!-- Badges Section -->
      <div v-if="badges.length > 0" class="badges-section">
        <h2 class="cabin-family semi-bold">Your Achievements</h2>
        <div class="badges-grid">
          <div
            v-for="badge in badges"
            :key="badge.id"
            class="badge-card"
            :class="`badge-${badge.badge_type}`"
          >
            <div class="badge-icon">
              <i class="fas fa-trophy"></i>
            </div>
            <div class="badge-info">
              <h4 class="cabin-family semi-bold">{{ badge.badge_type.toUpperCase() }}</h4>
              <p class="secondary-text">{{ formatDate(badge.earned_at) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <GoalCreationWizard
      :visible="showCreateGoalModal"
      @update:visible="showCreateGoalModal = $event"
      @goal-created="handleGoalCreated"
    />

    <ContributionModal
      :visible="showContributionModal"
      :goal="selectedGoal"
      @update:visible="handleModalVisibilityChange"
      @contribution-success="handleContributionSuccess"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { withWalletTypeParams } from '@/utils/walletType';
import { formatCurrency, formatDate } from '../utils/formatters';
import { LoadingSpinner } from '../components/shared';
import { GoalCard, GoalCreationWizard, ContributionModal } from '../components/goals';

export default {
  name: 'Goals',
  components: {
    LoadingSpinner,
    GoalCard,
    GoalCreationWizard,
    ContributionModal,
  },
  data() {
    return {
      showCreateGoalModal: false,
      showContributionModal: false,
      selectedGoalId: null,
      searchQuery: '',
      selectedFilter: 'all',
      selectedSort: 'created_desc',
      currentPage: 1,
      itemsPerPage: 12,
      loadingMore: false,
      goalInterestRate: null,
    };
  },
  computed: {
    ...mapState('goals', ['goals', 'badges', 'loading', 'error']),
    ...mapGetters('goals', [
      'activeGoals',
      'completedGoals',
      'totalGoalValue',
      'totalSavedAmount',
      'overallProgress',
    ]),

    selectedGoal() {
      if (!this.selectedGoalId) return null;
      return this.goals.find((goal) => goal.id === this.selectedGoalId);
    },

    allFilteredGoals() {
      let filtered = [...this.goals];

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter((goal) => (
          goal.title.toLowerCase().includes(query)
          || goal.description?.toLowerCase().includes(query)
          || goal.goal_type?.name.toLowerCase().includes(query)
        ));
      }

      // Apply status filter
      switch (this.selectedFilter) {
        case 'active':
          filtered = filtered.filter((goal) => goal.status === 'active');
          break;
        case 'completed':
          filtered = filtered.filter((goal) => goal.status === 'completed');
          break;
        case 'on_track':
          filtered = filtered.filter((goal) => goal.is_on_track);
          break;
        case 'behind':
          filtered = filtered.filter((goal) => !goal.is_on_track && goal.status === 'active');
          break;
        default:
          break;
      }

      // Apply sorting
      switch (this.selectedSort) {
        case 'created_asc':
          filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          break;
        case 'created_desc':
          filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          break;
        case 'progress_asc':
          filtered.sort((a, b) => (a.progress_percentage || 0) - (b.progress_percentage || 0));
          break;
        case 'progress_desc':
          filtered.sort((a, b) => (b.progress_percentage || 0) - (a.progress_percentage || 0));
          break;
        case 'target_date_asc':
          filtered.sort((a, b) => new Date(a.target_date) - new Date(b.target_date));
          break;
        default:
          break;
      }

      return filtered;
    },

    paginatedGoals() {
      return this.allFilteredGoals.slice(0, this.currentPage * this.itemsPerPage);
    },

    hasMoreGoals() {
      return this.allFilteredGoals.length > this.currentPage * this.itemsPerPage;
    },
  },
  async mounted() {
    await this.loadGoals();
    await this.loadBadges();
    this.fetchGoalRate();
  },
  methods: {
    ...mapActions('goals', ['fetchGoals', 'fetchUserBadges']),

    formatCurrency,
    formatDate,

    async loadGoals() {
      await this.fetchGoals();
    },

    async loadBadges() {
      await this.fetchUserBadges();
    },

    loadMoreGoals() {
      this.loadingMore = true;
      this.currentPage += 1;
      // Reduced artificial delay for better UX
      setTimeout(() => {
        this.loadingMore = false;
      }, 300);
    },

    handleContribute(goal) {
      this.selectedGoalId = goal.id;
      this.showContributionModal = true;
    },

    handleViewDetails(goal) {
      // Navigate to goal details page
      this.$router.push(`/goals/${goal.id}`);
    },

    handleGoalCreated(goal) {
      // Goal is automatically added to store via the action
      this.$toast.success(`Goal "${goal.title}" created successfully!`);
    },

    handleContributionSuccess() {
      // Goal progress is automatically updated via the store action
      this.$toast.success('Contribution added successfully!');
    },

    handleModalVisibilityChange(visible) {
      // Only close the modal if user manually closes it
      // Don't close it automatically after successful contribution
      if (!visible) {
        this.showContributionModal = false;
        this.selectedGoalId = null; // Clear selected goal when manually closed
      }
    },

    clearFilters() {
      this.searchQuery = '';
      this.selectedFilter = 'all';
      this.selectedSort = 'created_desc';
      this.currentPage = 1;
    },

    async fetchGoalRate() {
      try {
        const params = withWalletTypeParams(this.$store);
        const res = await this.$http({ url: `${this.$baseUrl}/savings-plans`, method: 'GET', params });
        const goalPlan = res.data.plans.find((p) => p.id === 5);
        if (goalPlan) this.goalInterestRate = goalPlan.percentage;
      } catch (e) {
        // fail silently — template shows fallback dash if null
      }
    },

    goBack() {
      this.$router.push('/');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variable.scss';

.goals-page {
  min-height: 100vh;
  background: #F9F9F9;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 1rem;

  .header-content h1 {
    margin-bottom: 0.5rem;
    color: $black;
  }

  .header-top {
    margin-bottom: 0.5rem;
  }

  .interest-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.6rem;
    padding: 0.4rem 0.9rem;
    background: linear-gradient(135deg, rgba($primary, 0.1) 0%, rgba($primary, 0.05) 100%);
    border: 1px solid rgba($primary, 0.25);
    border-radius: 20px;
    font-size: 13px;
    color: $primary;

    i {
      font-size: 12px;
    }

    strong {
      font-weight: 700;
    }
  }

  .back-btn {
    background: transparent;
    border: 1px solid $secondary;
    color: $secondary;
    padding: 0.5rem 1rem;
    border-radius: 6px;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s ease;

    &:hover {
      background: $secondary;
      color: $white;
    }

    i {
      font-size: 12px;
    }
  }

  .header-actions {
    flex-shrink: 0;
  }
}

.glass-morphism {
  background: rgba(255, 255, 255, 0.7) !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

// Micro-animations for the grid
.goals-list-enter-active,
.goals-list-leave-active {
  transition: all 0.5s ease;
}
.goals-list-enter-from,
.goals-list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}
.goals-list-move {
  transition: transform 0.5s ease;
}

.overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.overview-card {
  background: $white;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(224, 224, 224, 0.5);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
  }

  .card-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;

    &.primary {
      background: linear-gradient(135deg, $primary-light 0%, rgba($primary, 0.1) 100%);
      color: $primary;
    }

    &.success {
      background: linear-gradient(135deg, rgba($success, 0.2) 0%, rgba($success, 0.05) 100%);
      color: $success;
    }

    &.secondary {
      background: linear-gradient(135deg, rgba($secondary, 0.2) 0%, rgba($secondary, 0.05) 100%);
      color: $secondary;
    }
  }

  .card-content {
    flex: 1;
    min-width: 0;

    h3 {
      margin-bottom: 0.25rem;
      color: $black;
      font-size: 24px;
    }

    p {
      margin-bottom: 0;
      font-size: 14px;
    }
  }
}

.goals-controls {
  margin-bottom: 2rem;
}

.search-filter {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;

  i {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: $secondary;
    z-index: 2;
  }

  input {
    padding-left: 40px;
  }
}

.filter-dropdown,
.sort-dropdown {
  min-width: 150px;
}

.goals-content {
  margin-bottom: 3rem;
}

.loading-state,
.error-state,
.empty-state,
.no-results-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.error-card,
.empty-card {
  background: $white;
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  max-width: 400px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  i {
    font-size: 28px;
    color: $secondary;
    margin-bottom: 1rem;
  }

  h3 {
    margin-bottom: 1rem;
    color: $black;
  }

  p {
    margin-bottom: 1.5rem;
    color: $secondary;
  }
}

.goals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.load-more {
  display: flex;
  justify-content: center;
  margin-top: 2rem;

  .btn {
    min-width: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }
}

.badges-section {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #E0E0E0;

  h2 {
    margin-bottom: 1.5rem;
    color: $black;
  }
}

.badges-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.badge-card {
  background: $white;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &.badge-bronze {
    border-left: 4px solid #CD7F32;
  }

  &.badge-silver {
    border-left: 4px solid #C0C0C0;
  }

  &.badge-gold {
    border-left: 4px solid #FFD700;
  }

  &.badge-platinum {
    border-left: 4px solid #E5E4E2;
  }

  .badge-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: $primary-light;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $primary;
    font-size: 16px;
  }

  .badge-info {
    flex: 1;

    h4 {
      margin-bottom: 0.25rem;
      color: $black;
      font-size: 14px;
    }

    p {
      margin-bottom: 0;
      font-size: 12px;
    }
  }
}

@media (max-width: 768px) {
  .goals-page .content {
    padding: 1rem;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;

    .header-actions {
      align-self: stretch;
    }
  }

  .overview-cards {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  }

  .search-filter {
    flex-direction: column;

    .search-box {
      min-width: unset;
    }

    .filter-dropdown,
    .sort-dropdown {
      min-width: unset;
    }
  }

  .goals-grid {
    grid-template-columns: 1fr;
  }

  .badges-grid {
    grid-template-columns: 1fr;
  }
}
</style>
